import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useServiceJobStatusAutocompleteQuery } from '../../../../../queries';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../Checkbox';
import { Dropdown } from '../../../Dropdown';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet as dropdownStyles } from '../../DropdownComponent/styles';
import { styleSheet } from './styles';

export const ServiceJobStatusFilterDropdown = ({
	selectedStatuses,
	setSelectedStatuses,
	anchorClassName,
	styles,
}: {
	selectedStatuses: Api.IServiceJobStatus[];
	setSelectedStatuses: React.Dispatch<React.SetStateAction<Api.IServiceJobStatus[]>>;
	anchorClassName?: string;
	styles?: StyleDeclarationValue[];
}) => {
	const [isOpen, setIsOpen] = React.useState(false);

	const statusAutocompleteQuery = useServiceJobStatusAutocompleteQuery({
		enabled: true,
		pageSize: 25,
		fragment: '',
	});

	const statuses = React.useMemo(() => {
		return statusAutocompleteQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [statusAutocompleteQuery.data]);

	const dropdownMessage = React.useMemo(() => {
		if (selectedStatuses.length === 0) {
			return 'Status';
		}
		return selectedStatuses.length === 1
			? selectedStatuses[0]?.name
			: `${selectedStatuses[0]?.name} +${selectedStatuses.length - 1}`;
	}, [selectedStatuses]);

	if (statusAutocompleteQuery.isFetched && statuses.length === 0) {
		return null;
	}

	const resetFilter = () => {
		setSelectedStatuses([]);
	};

	const isStatusSelected = (status: Api.IServiceJobStatus) => {
		return selectedStatuses.some(s => s.id === status.id);
	};

	const onToggleStatus = (status: Api.IServiceJobStatus) => {
		const isSelected = isStatusSelected(status);

		if (isSelected) {
			setSelectedStatuses(selectedStatuses.filter(s => s.id !== status.id));
		} else {
			setSelectedStatuses([...selectedStatuses, status]);
		}
	};

	return (
		<Dropdown
			anchor={
				<div className={css(dropdownStyles.dropdownAnchor)}>
					<span>{dropdownMessage}</span>
					<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
				</div>
			}
			anchorClassName={anchorClassName}
			disabled={false}
			className={css(dropdownStyles.dropdownInline, isOpen ? dropdownStyles.dropdownInlineOpen : null)}
			contentClassName={css(dropdownStyles.dropdownMenu)}
			contentPositionY='bottom'
			contentInnerClassName={css(styleSheet.dropdownContentInnerOverrides)}
			isOpen={isOpen}
			onOpenChanged={() => setIsOpen(!isOpen)}
			openOnClick
			styles={styles}
		>
			<div className={css(dropdownStyles.dropdown)}>
				<div>
					{statuses.map(status => {
						return (
							<div className={css(styleSheet.dropdownRow)} key={`status-select-${status.id}`}>
								<Checkbox
									type='large'
									checked={isStatusSelected(status)}
									id={status.id}
									onChange={() => onToggleStatus(status)}
								/>
								<span>{status.name}</span>
							</div>
						);
					})}
				</div>

				{statusAutocompleteQuery.hasNextPage && !statusAutocompleteQuery.isFetchingNextPage && (
					<Waypoint onEnter={() => statusAutocompleteQuery.fetchNextPage()} />
				)}
				<div className={css(dropdownStyles.dropdownSeparator)} />

				<Button variant='link' onClick={resetFilter}>
					Reset Filter
				</Button>
			</div>
		</Dropdown>
	);
};
